"use client";

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CreditCard, ShoppingCart, Wallet } from "lucide-react";
import Navbar from "../../../../../components/navbar.jsx";
import Footer from "../../../../../components/footer.jsx";
import Button from "../../../../../components/Button.jsx";
import styles from "../css/creditProfiles.module.css";
import ConfirmPaymentModal from "../../../components/ConfirmPaymentModal.jsx";
import { previewCredit as apiPreviewCredit, confirmCredit as apiConfirmCredit, getSelectedCustomer as apiGetSelectedCustomer, getCart as apiGetCart } from "../../../../../api/frontShop/CheckOutAPI.js";

export default function CreditBalanceView() {
  const location = useLocation();
  const navigate = useNavigate();

  const [purchaseAmount, setPurchaseAmount] = useState(
    typeof location.state?.total === "number" ? location.state.total : 0
  );
  const fromDebtors = !!(location.state && location.state.fromDebtors);
  const [outstandingBalance, setOutstandingBalance] = useState(0);
  const [newOutstandingBalance, setNewOutstandingBalance] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function loadPreview() {
      setError("");
      try {
        // Ensure prerequisites are met before hitting preview endpoint
        const [{ data: selected }, { data: cart }] = await Promise.all([
          apiGetSelectedCustomer(),
          apiGetCart(),
        ]);
        const selectedId = selected?.patientid || null;
        const items = Array.isArray(cart?.items) ? cart.items : [];

        if (!selectedId) {
          setError("Please select a customer before using Credit payment.");
          return;
        }
        if (items.length === 0) {
          setError("Your cart is empty. Add items before using Credit payment.");
          return;
        }

        const { data: json } = await apiPreviewCredit();
        if (!json?.success || !json?.data) return;
        if (cancelled) return;
        const bal = Number(json.data.balanceOutstanding || 0);
        const amt = Number(json.data.purchaseAmount || 0);
        const newBal = Number(json.data.newBalanceOutstanding || 0);
        setOutstandingBalance(Number.isFinite(bal) ? bal : 0);
        setPurchaseAmount(Number.isFinite(amt) ? amt : 0);
        setNewOutstandingBalance(Number.isFinite(newBal) ? newBal : 0);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load credit balance");
      }
    }
    loadPreview();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleBack = () => {
    navigate("/frontshop/payment-types", {
      state: { total: purchaseAmount, fromDebtors },
    });
  };

  const handleProceed = () => {
    setShowConfirm(true);
  };

  const handleConfirmPayment = async () => {
    setLoading(true);
    setError("");
    try {
      const { data: json } = await apiConfirmCredit();
      const recieptno = json?.data?.recieptno ? String(json.data.recieptno) : null;
      const paid =
        json?.data && typeof json.data.amountPaid === "number"
          ? json.data.amountPaid
          : Number(purchaseAmount) || 0;
      setShowConfirm(false);
      navigate("/frontshop/payment-types/credit/balance/complete", {
        state: {
          ...location.state,
          total: purchaseAmount,
          amountPaid: paid,
          recieptno,
        },
      });
    } catch (e) {
      setError(e?.message || "Failed to confirm credit payment");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
  };


  return (
    <div className={styles.wrapper}>
      <Navbar onBack={handleBack} />
      <main className={styles.main}>
        <div className={styles.balanceOverlay}>
          <div className={styles.balanceModal}>
            <h1 className={styles.balanceTitle}>Credit Balance</h1>

            <section className={styles.balanceInnerCard}>
              <div className={styles.balanceSummaryCard}>
                <div className={styles.balanceRows}>
                  <div className={styles.balanceRow}>
                    <div className={styles.balanceRowLabel}>
                      <CreditCard className={styles.balanceIcon} />
                      <span>Balance Outstanding</span>
                    </div>
                    <div className={styles.balanceRowAmount}>
                      <span className={styles.balanceCurrency}>R</span>
                      <span className={styles.balanceAmount}>
                        {outstandingBalance.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className={styles.balanceRow}>
                    <div className={styles.balanceRowLabel}>
                      <ShoppingCart className={styles.balanceIcon} />
                      <span>Purchase Amount</span>
                    </div>
                    <div className={styles.balanceRowAmount}>
                      <span className={styles.balanceCurrency}>R</span>
                      <span className={styles.balanceAmount}>
                        {purchaseAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className={styles.balanceDivider} />

                  <div className={styles.balanceRow}>
                    <div className={styles.balanceRowLabel}>
                      <Wallet className={styles.balanceIcon} />
                      <span>New Balance Outstanding</span>
                    </div>
                    <div className={styles.balanceRowAmount}>
                      <span className={styles.balanceCurrency}>R</span>
                      <span className={styles.balanceAmount}>
                        {newOutstandingBalance.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div className={styles.balanceButtonsRow}>
              <Button
                type="button"
                variant="cancel"
                className={styles.balanceBackButton}
                onClick={handleBack}
              >
                ← Back
              </Button>
              <Button
                type="button"
                variant="approve"
                className={styles.balanceProceedButton}
                onClick={handleProceed}
                disabled={loading}
              >
                Proceed
              </Button>
            </div>
          </div>
        </div>
      </main>
      <div className={styles.footerWrapper}>
        <Footer />
      </div>
      {error && (
        <div className={styles.errorBanner}>{error}</div>
      )}
      {showConfirm && (
        <ConfirmPaymentModal
          total={purchaseAmount}
          onConfirm={handleConfirmPayment}
          onCancel={handleCancelConfirm}
        />
      )}
    </div>
  );
}
